/**
 * A new hire's software or folder (14 Oct, Rusty: "software and folders are
 * part of the New User process"): the item form's own fields, asked on the
 * checklist — the title, path or site first, then the Software item's device
 * and why, or the Folder item's type, access level and why. Labels and
 * choices come from the item form itself, so they read as the form does.
 *
 * Nothing is filed for these: Global IT sets them up with the account, and a
 * listed title or folder (IT Admin → Approvers → Resource owners) is approved
 * by its owner first. Every field is optional, as on the form.
 */

import { Field, Input, Textarea } from "@norican/react";

import { LookupSelect, useRequestOffering, useTranslateTo } from "@/features/tickets";
import { useT } from "@/i18n";
import type { AccessCatalogItem, OfferingParameter } from "@/lib/api";

import { type NeedDetails, RESOURCE_FIELD } from "../lib/access";

export interface NewHireItemFieldsProps {
  item: AccessCatalogItem;
  /** The resource (title, path or URL): the item's note. */
  resource: string;
  details: NeedDetails;
  onResource: (value: string) => void;
  onDetail: (field: string, value: string) => void;
}

/** The option's text as the form stores it (never a translation). */
function optionText(option: NonNullable<OfferingParameter["options"]>[number]): string {
  return option.label.original_text ?? option.label.text;
}

export function NewHireItemFields({
  item,
  resource,
  details,
  onResource,
  onDetail,
}: NewHireItemFieldsProps) {
  const { t } = useT("tickets");
  const translateTo = useTranslateTo();
  const offering = useRequestOffering(item.offering_id ?? null, translateTo);
  const parameters = offering.data?.parameters ?? [];
  const fields = item.new_hire_fields ?? [];
  const byName = new Map(parameters.map((parameter) => [parameter.internal_name, parameter]));
  return (
    <>
      {fields.map((field) => {
        const name = `access-${item.key}-${field}`;
        if (field === RESOURCE_FIELD) {
          return (
            <Field
              key={field}
              name={name}
              label={t(`access.wizard.items.resource.${item.key}` as never, {
                defaultValue: t("access.wizard.items.noteLabel"),
              })}
              hint={t("access.wizard.items.resourceHint")}
            >
              <Input
                value={resource}
                maxLength={500}
                onChange={(event) => onResource(event.target.value)}
              />
            </Field>
          );
        }
        const parameter = byName.get(field);
        const label =
          parameter?.name.text ??
          t(`access.wizard.items.field.${field}` as never, { defaultValue: field });
        const value = details[field] ?? "";
        if (parameter?.type === "SELECT" && (parameter.options ?? []).length > 0) {
          return (
            <Field key={field} name={name} label={label}>
              <LookupSelect
                name={name}
                items={(parameter.options ?? []).map((option) => ({
                  id: optionText(option),
                  label: option.label,
                }))}
                value={value === "" ? null : value}
                onValueChange={(next) => onDetail(field, next ?? "")}
                placeholder={label}
                clearLabel={t("access.wizard.items.notSaid")}
                aria-label={label}
              />
            </Field>
          );
        }
        return (
          <Field key={field} name={name} label={label}>
            {parameter?.type === "TEXTAREA" ? (
              <Textarea
                value={value}
                maxLength={1000}
                onChange={(event) => onDetail(field, event.target.value)}
              />
            ) : (
              <Input
                value={value}
                maxLength={200}
                onChange={(event) => onDetail(field, event.target.value)}
              />
            )}
          </Field>
        );
      })}
    </>
  );
}
